import { useState, useRef } from 'react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { BookOpen, FileText, MessageSquare, Shuffle, Upload, X, FileUp, AlertCircle } from 'lucide-react'
import type { ScenarioInput } from '@creanizer/types'
import { readFileAsText, readMultipleFiles, formatFileSize, FILE_ACCEPT } from '../../utils/fileReader'

interface SystemAnalysisModeProps {
  input: ScenarioInput
  setInput: (input: ScenarioInput) => void
}

export function SystemAnalysisMode({ input, setInput }: SystemAnalysisModeProps) {
  const settings = input.systemAnalysisSettings || {
    sourceType: 'rulebook',
    sourceText: '',
    includeExamples: true
  }
  
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const updateSettings = (updates: Partial<ScenarioInput['systemAnalysisSettings']>) => {
    setInput({
      ...input,
      systemAnalysisSettings: {
        ...settings,
        ...updates
      }
    })
  }
  
  // ファイルアップロード処理
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    setIsProcessingFiles(true)
    setFileErrors([])
    
    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      if (errors.length > 0) {
        setFileErrors(errors)
      }
      
      if (combinedText) {
        // 既存のテキストがある場合は追加
        const newText = settings.sourceText 
          ? settings.sourceText + '\n\n---\n\n' + combinedText
          : combinedText
        updateSettings({ sourceText: newText })
        
        // 成功したファイルを保存
        const successFiles = fileInfos
          .filter(info => !info.error)
          .map((info, index) => files[index])
          .filter(Boolean)
        setUploadedFiles(prev => [...prev, ...successFiles])
      }
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      setFileErrors(['ファイルの読み込みに失敗しました'])
    } finally {
      setIsProcessingFiles(false)
      // ファイル入力をリセット
      if (event.target) {
        event.target.value = ''
      }
    }
  }
  
  // ファイル削除
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }
  
  return (
    <div className="space-y-6">
      <Alert>
        <BookOpen className="h-4 w-4" />
        <AlertDescription>
          TRPGのルールブックやプレイログからシステムルールを解析し、保存します。
          保存したシステムはシナリオ生成時に利用できます。
        </AlertDescription>
      </Alert>
      
      <Card>
        <CardHeader>
          <CardTitle>システム情報</CardTitle>
          <CardDescription>
            解析するシステムの基本情報を入力してください
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="system-name">システム名（オプション）</Label>
            <Input
              id="system-name"
              value={settings.systemName || ''}
              onChange={(e) => updateSettings({ systemName: e.target.value })}
              placeholder="例: D&D5e, クトゥルフ神話TRPG"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="system-version">バージョン（オプション）</Label>
            <Input
              id="system-version"
              value={settings.systemVersion || ''}
              onChange={(e) => updateSettings({ systemVersion: e.target.value })}
              placeholder="例: 7版, 2.0"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="source-type">ソースタイプ</Label>
            <Select 
              value={settings.sourceType} 
              onValueChange={(value) => updateSettings({ sourceType: value as any })}
            >
              <SelectTrigger id="source-type" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rulebook">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    ルールブック
                  </div>
                </SelectItem>
                <SelectItem value="play_log">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    プレイログ
                  </div>
                </SelectItem>
                <SelectItem value="description">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    説明文
                  </div>
                </SelectItem>
                <SelectItem value="mixed">
                  <div className="flex items-center gap-2">
                    <Shuffle className="w-4 h-4" />
                    混合
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              {settings.sourceType === 'rulebook' && (
                <>ルールブックからコアルール、判定方法、キャラクター作成方法などを抽出します。</>
              )}
              {settings.sourceType === 'play_log' && (
                <>実際のプレイログから使用されているルールやシステムを推測・抽出します。</>
              )}
              {settings.sourceType === 'description' && (
                <>システムの説明文からルールと特徴を整理して抽出します。</>
              )}
              {settings.sourceType === 'mixed' && (
                <>複数のソースタイプが混在したテキストからシステムを解析します。</>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>解析対象テキスト</CardTitle>
          <CardDescription>
            システムのルールや説明が含まれるテキストを入力してください
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ファイルアップロードセクション */}
          <div className="space-y-3">
            <Label>ファイルから読み込み</Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPT}
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessingFiles}
              >
                <Upload className="w-4 h-4 mr-2" />
                ファイルを選択
              </Button>
              <p className="text-sm text-gray-500 mt-2">
                サポート形式：.txt, .docx, .pdf, .md
              </p>
              <p className="text-xs text-gray-400 mt-1">
                複数ファイルを選択できます
              </p>
            </div>
            
            {/* アップロード済みファイル一覧 */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">読み込み済みファイル</Label>
                <div className="space-y-1">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">{file.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {formatFileSize(file.size)}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* エラー表示 */}
            {fileErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1">
                    {fileErrors.map((error, index) => (
                      <li key={index} className="text-sm">{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <div>
            <Label htmlFor="source-text">テキスト入力</Label>
            <Textarea
              id="source-text"
              value={settings.sourceText}
              onChange={(e) => updateSettings({ sourceText: e.target.value })}
              placeholder={getPlaceholderText(settings.sourceType)}
              rows={15}
              className="font-mono text-sm mt-2"
            />
            <p className="text-xs text-gray-500 mt-2">
              {settings.sourceText.length.toLocaleString()}文字
            </p>
          </div>
          
          <div className="space-y-3">
            <Label>抽出重点項目（オプション）</Label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'dice', label: 'ダイスシステム' },
                { id: 'attributes', label: '能力値・属性' },
                { id: 'skills', label: 'スキル・技能' },
                { id: 'combat', label: '戦闘ルール' },
                { id: 'magic', label: '魔法・特殊能力' },
                { id: 'character', label: 'キャラクター作成' },
                { id: 'advancement', label: '成長・レベルアップ' },
                { id: 'items', label: 'アイテム・装備' }
              ].map((item) => {
                const isChecked = settings.extractionFocus?.includes(item.id) || false
                return (
                  <div key={item.id} className="flex items-center gap-2">
                    <Checkbox
                      id={item.id}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        const current = settings.extractionFocus || []
                        if (checked) {
                          updateSettings({ extractionFocus: [...current, item.id] })
                        } else {
                          updateSettings({ extractionFocus: current.filter(f => f !== item.id) })
                        }
                      }}
                    />
                    <Label htmlFor={item.id} className="text-sm">
                      {item.label}
                    </Label>
                  </div>
                )
              })}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-examples"
              checked={settings.includeExamples}
              onCheckedChange={(checked) => updateSettings({ includeExamples: checked === true })}
            />
            <Label htmlFor="include-examples">
              実例を含めて解析（より詳細な結果になります）
            </Label>
          </div>

          <div className="pt-4 border-t space-y-3">
            <Label className="text-sm font-medium">AIモデル選択</Label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="useGemini3Mode"
                checked={settings.useGemini3Mode || false}
                onCheckedChange={(checked) => updateSettings({
                  useGemini3Mode: !!checked,
                  useGemini3FlashMode: checked ? false : settings.useGemini3FlashMode
                })}
              />
              <Label htmlFor="useGemini3Mode" className="text-sm">
                Gemini 3 モード（有料APIのみ・試験版）
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="useGemini3FlashMode"
                checked={settings.useGemini3FlashMode || false}
                onCheckedChange={(checked) => updateSettings({
                  useGemini3FlashMode: !!checked,
                  useGemini3Mode: checked ? false : settings.useGemini3Mode
                })}
              />
              <Label htmlFor="useGemini3FlashMode" className="text-sm">
                Gemini 3 Flash モード（有料APIのみ・試験版・高速）
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function getPlaceholderText(sourceType: string): string {
  switch (sourceType) {
    case 'rulebook':
      return `ルールブックのテキストを貼り付けてください。

例:
第1章 基本ルール
判定の基本
このゲームでは、すべての行動判定は2d6+能力値で行います。
目標値以上の値を出せば成功となります。

能力値
キャラクターは以下の6つの能力値を持ちます:
- 筋力 (STR): 物理的な力
- 敏捷 (DEX): 素早さと器用さ
...`
    
    case 'play_log':
      return `実際のプレイログを貼り付けてください。

例:
GM: では、罠を解除しようとするなら器用さで判定してください。目標値は15です。
プレイヤー: 2d6を振ります... 出目は8、器用さ+3で合計11です。
GM: 残念、失敗です。罠が作動して...
...`
    
    case 'description':
      return `システムの説明文を貼り付けてください。

例:
このTRPGシステムは、2d6を使用したシンプルな判定システムを採用しています。
キャラクターは6つの基本能力値を持ち、それぞれの能力値は-3から+3の範囲で設定されます。
...`
    
    default:
      return 'システムに関するテキストを入力してください'
  }
}